/**
 * WebPhoneProvider — wires the JsSIP WebPhone to the React tree.
 *
 * Responsibilities:
 *   • Pulls SIP config from /api/softphone/config and registers the UA
 *   • Maintains an authenticated WebSocket signalling channel
 *   • Coordinates live-listen and whisper peer connections between
 *     the agent (whose JsSIP call is active) and admins watching the
 *     Communicator > Live Calls panel.
 *   • Auto-uploads the recording at the end of every WebPhone call
 *
 * The provider is a no-op until the user is logged in and the tenant
 * SIP config is `enabled` + non-empty.
 */
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { getWebPhone } from '../lib/webphone';
import { useAuth } from './AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';

const WebPhoneContext = createContext(null);
export const useWebPhone = () => useContext(WebPhoneContext);

const ICE = { iceServers: [{ urls: ['stun:stun.l.google.com:19302'] }] };

export function WebPhoneProvider({ children }) {
  const { user } = useAuth() || {};
  const token = (typeof window !== 'undefined') ? localStorage.getItem('crm_token') : null;
  const [registered, setRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState(null);
  const [config, setConfig] = useState(null);
  const [activeCall, setActiveCall] = useState(null); // {id, lead_id, lead_name, phone_e164, status, startedAt}
  const [muted, setMuted] = useState(false);
  const [lastEndedCall, setLastEndedCall] = useState(null); // {id, lead_id, lead_name, phone_e164, duration, dispositioned}

  const wpRef = useRef(getWebPhone());
  const wsRef = useRef(null);
  const wsOpenRef = useRef(false);
  // Keep latest activeCall accessible inside event handlers (which close over
  // stale state). Synced via the effect below.
  const activeCallRef = useRef(null);
  // Live listen / whisper peer connections (admin <-> agent)
  // On the AGENT side: peerConns[adminUserId] = { listenPc, whisperPc }
  // On the ADMIN side: peerConns[agentUserId] = { listenPc, whisperPc }
  const peerConnsRef = useRef({});
  // ICE candidate queue when remote description is not yet set
  const iceQueueRef = useRef({});

  // ── Helpers ───────────────────────────────────────────────────────────
  const sendWS = useCallback((payload) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(payload));
      return true;
    }
    return false;
  }, []);

  // ── Bring up WebSocket signalling (independent of SIP register) ───────
  useEffect(() => {
    if (!user || !token) return;
    const backend = process.env.REACT_APP_BACKEND_URL || '';
    const wsBase = backend.replace(/^http/, 'ws');
    const ws = new WebSocket(`${wsBase}/api/softphone/ws/signal?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;
    ws.onopen = () => { wsOpenRef.current = true; };
    ws.onclose = () => { wsOpenRef.current = false; };
    ws.onerror = () => { /* surface silently */ };
    ws.onmessage = (e) => {
      try { handleSignal(JSON.parse(e.data)); } catch (err) { /* ignore */ }
    };
    return () => { try { ws.close(); } catch { /* noop */ } };
  }, [user?.id || user?.user_id, token]);

  // ── SIP registration when config + user are ready ────────────────────
  const handlersAttachedRef = useRef(false);
  const attachHandlers = useCallback(() => {
    if (handlersAttachedRef.current) return;
    handlersAttachedRef.current = true;
    const wp = wpRef.current;
    wp.on('registered', () => { setRegistered(true); setRegistering(false); setError(null); });
    wp.on('unregistered', () => setRegistered(false));
    wp.on('registrationFailed', (e) => {
      setRegistered(false); setRegistering(false);
      setError(e?.cause || 'Registration failed');
    });
    wp.on('confirmed', () => setActiveCall((c) => c ? { ...c, status: 'in_progress' } : c));
    wp.on('progress', () => setActiveCall((c) => c ? { ...c, status: 'ringing' } : c));
    wp.on('failed', () => {
      const ended = activeCallRef.current;
      setActiveCall(null);
      sendWS({ to: null, type: 'call-ended', data: {} });
      if (ended) {
        setLastEndedCall({ id: ended.id, lead_id: ended.lead_id, lead_name: ended.lead_name, phone_e164: ended.phone_e164, duration: 0, dispositioned: false });
      }
    });
    wp.on('ended', () => {
      const ended = activeCallRef.current;
      sendWS({ to: null, type: 'call-ended', data: {} });
      setActiveCall(null);
      if (ended) {
        const duration = Math.max(0, Math.floor((Date.now() - (ended.startedAt || Date.now())) / 1000));
        setLastEndedCall({ id: ended.id, lead_id: ended.lead_id, lead_name: ended.lead_name, phone_e164: ended.phone_e164, duration, dispositioned: false });
        api.patch(`/softphone/calls/${ended.id}`, { duration_seconds: duration, status: 'completed' }).catch(() => {});
      }
    });
    wp.on('recordingReady', async ({ blob, callId }) => {
      try {
        const fd = new FormData();
        fd.append('file', blob, `${callId}.webm`);
        await api.post(`/softphone/calls/${callId}/recording`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('تم رفع تسجيل المكالمة');
      } catch (e) {
        toast.error('فشل رفع التسجيل');
      }
    });
  }, [sendWS]);

  const doRegister = useCallback(async (data) => {
    attachHandlers();
    setRegistering(true);
    await wpRef.current.register({
      wsUri: data.ws_url,
      sipUri: `sip:${data.username}@${data.server}`,
      password: await fetchPassword(),
      displayName: user?.name || user?.email || data.username,
    });
  }, [attachHandlers, user?.name, user?.email]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) return;
      const role = (user.role || '').toLowerCase();
      // Only auto-register a SIP UA for users who actually take calls.
      // Admins observe via WS only — they register on demand (ensureRegistered).
      if (role === 'admin' || role === 'superadmin') {
        try {
          const { data } = await api.get('/softphone/config');
          if (!cancelled) setConfig(data);
        } catch { /* ignore */ }
        return;
      }
      try {
        setRegistering(true);
        const { data } = await api.get('/softphone/config');
        if (cancelled) return;
        setConfig(data);
        if (!data?.enabled || !data?.ws_url || !data?.username) {
          setRegistering(false);
          return;
        }
        await doRegister(data);
      } catch (e) {
        setRegistering(false);
        setError(e?.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id || user?.user_id]);

  /** Register on demand (any role). Resolves true when registered, false on failure/timeout. */
  const lastRegFailRef = useRef(0);
  const ensureRegistered = useCallback(async (timeoutMs = 7000) => {
    const wp = wpRef.current;
    if (wp.isRegistered()) return true;
    if (Date.now() - lastRegFailRef.current < 5 * 60 * 1000) return false;
    let cfg = config;
    if (!cfg) { try { cfg = (await api.get('/softphone/config')).data; setConfig(cfg); } catch { return false; } }
    if (!cfg?.enabled || !cfg?.ws_url || !cfg?.username) return false;
    return new Promise((resolve) => {
      let settled = false;
      const finish = (v) => { if (!settled) { settled = true; offOk(); offBad(); setRegistering(false); if (!v) { lastRegFailRef.current = Date.now(); try { wp.unregister(); } catch { /* noop */ } } resolve(v); } };
      const offOk = wp.on('registered', () => finish(true));
      const offBad = wp.on('registrationFailed', () => finish(false));
      setTimeout(() => finish(wp.isRegistered()), timeoutMs);
      if (!wp.ua) doRegister(cfg).catch(() => finish(false));
    });
  }, [config, doRegister]);

  /** Password is intentionally write-only — admins set it in the SIP Config tab.
   * We fetch via a separate endpoint that returns it only to authenticated agents
   * of the same tenant. Falls back to the config response in dev. */
  async function fetchPassword() {
    try {
      const { data } = await api.get('/softphone/config-secret');
      return data?.password || '';
    } catch {
      return '';
    }
  }

  // ── Outbound call ─────────────────────────────────────────────────────
  const startCall = useCallback(async ({ lead_id, phone_e164, lead_name, callId }) => {
    const wp = wpRef.current;
    if (!wp.isRegistered()) {
      toast.error('WebPhone غير مسجّل — تحقق من إعدادات SIP');
      return;
    }
    // SIP URI to dial: route through the trunk, dialing E.164 as the user part.
    const server = config?.server || (wp.config?.sipUri || '').split('@')[1];
    const target = `sip:${phone_e164.replace('+', '')}@${server}`;
    const res = await wp.call(target, { id: callId, lead_id, phone_e164, lead_name });
    if (!res.ok) {
      toast.error(res.error || 'فشل بدء المكالمة');
      return;
    }
    setActiveCall({ id: callId, lead_id, lead_name, phone_e164, status: 'ringing', startedAt: Date.now() });
    sendWS({ to: null, type: 'call-started', data: { call_id: callId, lead_id, lead_name, phone_e164 } });
  }, [config, sendWS]);

  const hangup = useCallback(() => { wpRef.current.hangup(); }, []);
  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      wpRef.current.setMuted(next);
      return next;
    });
  }, []);

  // Sync ref so async wp event callbacks see the latest call
  useEffect(() => { activeCallRef.current = activeCall; }, [activeCall]);

  // ── Disposition (called from CallDispositionDialog) ──
  const submitDisposition = useCallback(async (option, extraNote) => {
    const c = lastEndedCall;
    if (!c) return;
    try {
      // 1. Pre-filled comment on the lead
      const note = (extraNote && extraNote.trim()) ? `${option.comment}\n${extraNote.trim()}` : option.comment;
      await api.post('/comments', { client_id: c.lead_id, content: note });
      // 2. Update softphone call status + notes
      await api.patch(`/softphone/calls/${c.id}`, { notes: option.label, status: option.key === 'no-answer' ? 'missed' : 'completed' });
      // 3. Update lead status / tag if disposition implies one
      const leadUpdate = {};
      if (option.status) leadUpdate.status = option.status;
      if (Object.keys(leadUpdate).length) {
        await api.patch(`/clients/${c.lead_id}`, leadUpdate);
      }
      if (option.tag) {
        try { await api.post('/clients/bulk-add-tag', { lead_ids: [c.lead_id], tag: option.tag }); } catch { /* tag endpoint optional */ }
      }
      toast.success('تم حفظ نتيجة المكالمة');
    } catch (e) {
      toast.error('فشل حفظ نتيجة المكالمة');
    } finally {
      setLastEndedCall((prev) => prev ? { ...prev, dispositioned: true } : prev);
      setTimeout(() => setLastEndedCall(null), 400);
    }
  }, [lastEndedCall]);

  const skipDisposition = useCallback(async () => {
    setLastEndedCall((prev) => prev ? { ...prev, dispositioned: true } : prev);
    setTimeout(() => setLastEndedCall(null), 100);
  }, []);

  // ════════════════════════════════════════════════════════════════════
  // Signalling: live listen + whisper
  // ════════════════════════════════════════════════════════════════════
  function getOrCreatePeer(remoteId, kind) {
    if (!peerConnsRef.current[remoteId]) peerConnsRef.current[remoteId] = {};
    const slot = peerConnsRef.current[remoteId];
    if (slot[kind]) return slot[kind];
    const pc = new RTCPeerConnection(ICE);
    pc.onicecandidate = (e) => {
      if (e.candidate) sendWS({ to: remoteId, type: 'webrtc-ice', data: { kind, candidate: e.candidate } });
    };
    slot[kind] = pc;
    return pc;
  }

  function closePeer(remoteId, kind) {
    const slot = peerConnsRef.current[remoteId];
    if (!slot || !slot[kind]) return;
    try { slot[kind].close(); } catch { /* noop */ }
    delete slot[kind];
  }

  async function handleSignal(msg) {
    const { from, type, data } = msg || {};
    if (!from || !type) return;

    // Admin-initiated: ask the agent to start sharing their combined audio.
    if (type === 'listen-request') {
      // We are the agent. Build offer.
      const wp = wpRef.current;
      const stream = wp.getCombinedStream();
      if (!stream) {
        sendWS({ to: from, type: 'error', data: { reason: 'no-active-call' } });
        return;
      }
      const pc = getOrCreatePeer(from, 'listen');
      // Add tracks
      stream.getAudioTracks().forEach((t) => pc.addTrack(t, stream));
      const offer = await pc.createOffer({ offerToReceiveAudio: false });
      await pc.setLocalDescription(offer);
      sendWS({ to: from, type: 'webrtc-offer', data: { kind: 'listen', sdp: offer } });
      return;
    }

    if (type === 'listen-stop') {
      closePeer(from, 'listen');
      return;
    }

    // Whisper: admin offers their mic to the agent
    if (type === 'whisper-start') {
      // We're the AGENT, accepting an inbound whisper from admin
      const pc = getOrCreatePeer(from, 'whisper');
      pc.ontrack = (e) => { wpRef.current.playWhisperStream(e.streams[0] || new MediaStream([e.track])); };
      // Wait for offer
      return;
    }
    if (type === 'whisper-stop') {
      closePeer(from, 'whisper');
      wpRef.current.stopWhisperStream();
      return;
    }

    if (type === 'webrtc-offer') {
      const kind = data?.kind || 'listen';
      const pc = getOrCreatePeer(from, kind);
      pc.ontrack = pc.ontrack || ((e) => {
        if (kind === 'whisper') {
          wpRef.current.playWhisperStream(e.streams[0] || new MediaStream([e.track]));
        } else if (kind === 'listen') {
          // ADMIN side — play through hidden audio element
          let el = document.getElementById(`webphone-listen-${from}`);
          if (!el) {
            el = document.createElement('audio');
            el.id = `webphone-listen-${from}`;
            el.autoplay = true;
            document.body.appendChild(el);
          }
          el.srcObject = e.streams[0] || new MediaStream([e.track]);
        }
      });
      await pc.setRemoteDescription(data.sdp);
      // Drain ICE queue
      const queued = iceQueueRef.current[`${from}:${kind}`] || [];
      for (const c of queued) {
        try { await pc.addIceCandidate(c); } catch { /* noop */ }
      }
      iceQueueRef.current[`${from}:${kind}`] = [];

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);
      sendWS({ to: from, type: 'webrtc-answer', data: { kind, sdp: answer } });
      return;
    }

    if (type === 'webrtc-answer') {
      const kind = data?.kind || 'listen';
      const pc = peerConnsRef.current[from]?.[kind];
      if (pc) await pc.setRemoteDescription(data.sdp);
      return;
    }

    if (type === 'webrtc-ice') {
      const kind = data?.kind || 'listen';
      const pc = peerConnsRef.current[from]?.[kind];
      if (pc && pc.remoteDescription) {
        try { await pc.addIceCandidate(data.candidate); } catch { /* noop */ }
      } else {
        // queue
        const key = `${from}:${kind}`;
        iceQueueRef.current[key] = iceQueueRef.current[key] || [];
        iceQueueRef.current[key].push(data.candidate);
      }
    }
  }

  // Admin actions
  const adminListen = useCallback((agentUserId) => {
    sendWS({ to: agentUserId, type: 'listen-request', data: {} });
  }, [sendWS]);
  const adminStopListen = useCallback((agentUserId) => {
    sendWS({ to: agentUserId, type: 'listen-stop', data: {} });
    closePeer(agentUserId, 'listen');
    const el = document.getElementById(`webphone-listen-${agentUserId}`);
    if (el) { el.srcObject = null; el.remove(); }
  }, [sendWS]);

  const adminWhisperStart = useCallback(async (agentUserId) => {
    sendWS({ to: agentUserId, type: 'whisper-start', data: {} });
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const pc = getOrCreatePeer(agentUserId, 'whisper');
    stream.getAudioTracks().forEach((t) => pc.addTrack(t, stream));
    const offer = await pc.createOffer({ offerToReceiveAudio: false });
    await pc.setLocalDescription(offer);
    sendWS({ to: agentUserId, type: 'webrtc-offer', data: { kind: 'whisper', sdp: offer } });
    // Store the stream so we can stop tracks later
    peerConnsRef.current[agentUserId] = peerConnsRef.current[agentUserId] || {};
    peerConnsRef.current[agentUserId].whisperStream = stream;
  }, [sendWS]);

  const adminWhisperStop = useCallback((agentUserId) => {
    sendWS({ to: agentUserId, type: 'whisper-stop', data: {} });
    const slot = peerConnsRef.current[agentUserId] || {};
    try { slot.whisperStream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    delete slot.whisperStream;
    closePeer(agentUserId, 'whisper');
  }, [sendWS]);

  const value = {
    registered,
    registering,
    error,
    config,
    activeCall,
    muted,
    lastEndedCall,
    startCall,
    ensureRegistered,
    hangup,
    toggleMute,
    submitDisposition,
    skipDisposition,
    adminListen,
    adminStopListen,
    adminWhisperStart,
    adminWhisperStop,
  };

  return <WebPhoneContext.Provider value={value}>{children}</WebPhoneContext.Provider>;
}
